<script lang="ts">
	import CheckIcon from "@lucide/svelte/icons/check";
	import Section from "#lib/web-ui/primitives/section.svelte";
	import {
		getTemplateContext,
		cloudinaryUrl,
		cloudinarySrcset
	} from "../template-context.svelte";

	const templateContext = getTemplateContext();
	const cashOfferSteps = [
		{
			label: "Submit Your Information",
			description:
				"Fill out our simple form with your property details and contact information. No obligation, just a first step to explore your options."
		},
		{
			label: "Receive Your Cash Offer",
			description:
				"Within 24 hours, we'll analyze your property and present you with a fair, no-obligation cash offer based on market conditions."
		},
		{
			label: "Choose Your Timeline",
			description:
				"Select a closing date that works for your schedule - as soon as 7 days or extended out to fit your moving plans and needs."
		},
		{
			label: "Close and Get Paid",
			description:
				"Sign the paperwork and receive your cash payment. No waiting for financing approvals or dealing with last-minute buyer issues."
		}
	];

	const perfectForItems = [
		"Properties requiring significant repairs or renovations",
		"Homeowners facing foreclosure or financial difficulties",
		"Out-of-state owners unable to manage distant properties",
		"Sellers needing to move quickly due to major life changes",
		"Inherited properties you don't wish to maintain or manage"
	];
</script>

<!-- Cash Offer Section -->
<Section class="bg-foreground/95">
	<!-- Title -->
	<div class="mb-10 w-full lg:mb-14">
		<h2 class="text-primary text-lg font-bold">Sell Fast. Get Cash.</h2>
		<h2 class="text-2xl font-semibold text-white md:text-4xl md:leading-tight">
			Quick Cash Offer Program
		</h2>
		<p class="mt-4 text-pretty text-neutral-400">
			Need to sell quickly with minimal hassle? Our Cash Offer Program provides a
			straightforward solution designed for homeowners who need certainty and speed. Unlike
			traditional real estate transactions that can take months with no guarantee of closing,
			our process eliminates the stress, delays, and uncertainty. We provide a fair,
			competitive offer within 24 hours and can close on your timeline.
		</p>
	</div>
	<!-- End Title -->

	<!-- Grid -->
	<div class="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
		<div class="aspect-w-16 aspect-h-9 lg:aspect-none">
			<img
				class="rounded-xl"
				src={cloudinaryUrl(templateContext.images.easyOffer, { width: 768 })}
				srcset={cloudinarySrcset(templateContext.images.easyOffer, 640)}
				alt="Quick Cash Offer"
				width="640"
				height="533"
				sizes="(max-width: 1024px) 100vw, 50vw"
				loading="eager"
				fetchpriority="high"
			/>
		</div>
		<!-- End Col -->

		<!-- Timeline -->
		<div>
			<!-- Heading -->
			<div class="mb-4">
				<h3 class="text-primary text-xs font-medium uppercase">Steps</h3>
			</div>
			<!-- End Heading -->
			{#each cashOfferSteps as step, i}
				<div class="ms-1 flex gap-x-5">
					<!-- Icon -->
					<div
						class="relative {i < cashOfferSteps.length - 1
							? 'after:absolute after:start-4 after:top-8 after:bottom-0 after:w-px after:-translate-x-[0.5px] after:bg-gray-200'
							: ''}"
					>
						<div class="relative z-10 flex size-8 items-center justify-center">
							<span
								class="border-secondary/30 text-primary flex size-8 shrink-0 items-center justify-center rounded-full border font-semibold uppercase"
							>
								{i + 1}
							</span>
						</div>
					</div>
					<!-- End Icon -->

					<!-- Right Content -->
					<div class="grow pt-0.5 pb-8 sm:pb-12">
						<p class="text-sm text-neutral-400 lg:text-base">
							<span class="text-white">{step.label}: </span>
							{step.description}
						</p>
					</div>
					<!-- End Right Content -->
				</div>
			{/each}
		</div>
		<!-- End Timeline -->
	</div>
	<!-- End Grid -->

	<!-- Perfect For Section -->
	<div class="border-secondary/10 mt-10 border-t pt-10">
		<div class="mb-6">
			<h3 class="text-primary text-xs font-medium uppercase">Perfect For</h3>
			<h4 class="mt-2 text-xl font-semibold text-white">
				Who Benefits Most From Our Cash Offer
			</h4>
		</div>

		<div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
			{#each perfectForItems as item}
				<div class="flex items-start gap-3 text-pretty">
					<div class="text-primary mt-1">
						<CheckIcon class="h-5 w-5" />
					</div>
					<div>
						<p class="text-white">{item}</p>
					</div>
				</div>
			{/each}
		</div>
	</div>
	<!-- End Perfect For Section -->
</Section>
